import React from "react";
import { Button, Typography } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import LoginImage from "../../assets/login_image.webp";
import SupportContact from "../../components/auth/SupportContact";

const { Title, Paragraph } = Typography;

/**
 * Branded landing for `/invitation-invalid`. Users arrive here when their
 * invitation or reset link is malformed, the token doesn't match what's
 * stored, or the account no longer exists. The page is intentionally generic
 * — we don't tell the user which of these failure modes applied, because
 * doing so would let attackers enumerate registered emails (an old version of
 * the `/users/verify` endpoint leaked this distinction in its error messages).
 *
 * `mode` (from `AcceptInvitePage`) picks the noun so a rejected password-reset
 * link doesn't read "invitation link". The recovery affordance is "contact
 * support" since we don't have a verified email to send a fresh link to.
 */
const InvitationInvalidPage = ({ mode = "invite" }) => {
  const navigate = useNavigate();
  const linkLabel = mode === "reset" ? "reset" : "invitation";

  const HeaderLogo = () => (
    <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
      Dash
      <span className="text-[1.25rem] font-semibold tracking-normal">
        ONE
      </span>
    </div>
  );

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <HeaderLogo />
      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">
          <div className="w-full flex flex-col gap-[40px]">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 bg-[#fff2f0] border border-[#ffccc7] rounded-xl flex items-center justify-center">
                <CloseCircleFilled className="text-[#cf1322] text-[1.5rem]" />
              </div>
              <Title
                level={2}
                className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
              >
                This {linkLabel} link is invalid
              </Title>
              <Paragraph className="!m-0 !text-[14px] !leading-[22px] !text-[#475467] font-normal">
                The link may have been mistyped, already used, or is no
                longer valid.
              </Paragraph>
            </div>

            <div className="flex flex-col gap-[16px]">
              <Button
                type="primary"
                block
                size="large"
                className="!rounded-[24px] !text-[16px] !font-normal !leading-[24px] h-12"
                onClick={() => navigate("/login")}
              >
                Go to Log in
              </Button>
              <div className="text-center">
                <SupportContact prefix="Need help?" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default InvitationInvalidPage;
